import type { MappingProposal } from "@/types";

/**
 * What the review screen says about a mapping proposal.
 *
 * Pure, because the rule is not a visual one: **which mapper produced this is
 * always stated**, and a fall-back to the weaker method is never silent. A
 * degradation nobody is told about looks like everything working until thirty
 * rows land in the wrong column.
 */
export interface MappingNotice {
  /** i18n key naming where the proposal came from. */
  sourceKey: string;
  confidenceKey: string;
  tone: string;
  /** True when the reader should look harder than usual. */
  cautious: boolean;
}

const CONFIDENCE_TONE: Record<MappingProposal["confidence"], string> = {
  high: "text-positive",
  medium: "text-warning",
  low: "text-danger",
};

export function mappingNotice(proposal: MappingProposal): MappingNotice {
  return {
    sourceKey:
      proposal.source === "ai" ? "import.mapping.fromAi" : "import.mapping.fromHeuristic",
    confidenceKey: `import.mapping.confidence.${proposal.confidence}`,
    tone: CONFIDENCE_TONE[proposal.confidence],
    // Either a weaker mapper or an unsure one is a reason to read every row
    // rather than skim. Both, obviously, more so.
    cautious: proposal.source === "heuristic" || proposal.confidence !== "high",
  };
}
